import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './Expand.scss';

// Expand / Collapse Animation Component
export default function Expand({
	showing = false,
	children,
	showTeaser = false,
	preserve = false,
}) {
	const contentRef = useRef(null);
	const teaserHeightRef = useRef(null);
	const prevShowingRef = useRef(showing);

	const [expanded, setExpanded] = useState(!!showing);
	const [animStyle, setAnimStyle] = useState({
		overflow: 'hidden',
		height: showing ? null : 0,
	});

	useEffect(() => {
		const prevShowing = prevShowingRef.current;
		prevShowingRef.current = showing;

		// Skip on initial render (no transition needed)
		if (prevShowing === showing) return;

		const timers = [];

		if (showing) {
			// Enter: remove display:none, then animate height from 0 to scrollHeight
			setExpanded(true);
			setAnimStyle({
				overflow: 'hidden',
				height: teaserHeightRef.current || 0,
			});

			// Wait for React to commit DOM (remove display:none) and browser to paint
			timers.push(
				setTimeout(() => {
					if (contentRef.current) {
						setAnimStyle({
							overflow: 'hidden',
							height: contentRef.current.scrollHeight,
						});
					}
				}, 30),
			);

			// Clear height/overflow constraint after transition completes
			timers.push(
				setTimeout(() => {
					setAnimStyle({ overflow: null, height: null });
				}, 380),
			);
		} else {
			// Exit: set current height, then animate to 0
			const currentHeight = contentRef.current
				? contentRef.current.scrollHeight
				: 0;
			setAnimStyle({ overflow: 'hidden', height: currentHeight });

			timers.push(
				setTimeout(() => {
					setAnimStyle({
						overflow: 'hidden',
						height: 0,
					});
				}, 30),
			);

			timers.push(
				setTimeout(() => {
					setExpanded(false);
				}, 380),
			);
		}

		return () => timers.forEach(clearTimeout);
	}, [showing]);

	const refCallback = useCallback(
		(node) => {
			contentRef.current = node;
			if (node && showTeaser && !showing) {
				teaserHeightRef.current = node.offsetHeight;
				setAnimStyle({
					overflow: 'hidden',
					height: teaserHeightRef.current,
				});
			}
		},
		[showTeaser, showing],
	);

	if (!(preserve || expanded || showTeaser)) return null;

	return React.cloneElement(children, {
		ref: refCallback,
		style: {
			...animStyle,
			transition: 'height 250ms ease-out',
		},
		className: classnames(children.props.className, 'expand-content', {
			collapsed: showTeaser && !expanded,
			expanded: showTeaser && expanded,
			'preserve-collapsed': preserve && !expanded,
		}),
	});
}

Expand.propTypes = {
	showing: PropTypes.bool,
	children: PropTypes.node,
	/** Shows the initial offset height of the child container instead of collapsing it */
	showTeaser: PropTypes.bool,
	preserve: PropTypes.bool,
};
